import {
  CssBaseline,
  ThemeProvider,
} from '@mui/material';
import { createMemoryRouter, RouterProvider } from 'react-router-dom';
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { devTools } from '@ngneat/elf-devtools';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@beeriot/localization';
import {
  setupAxiosInterceptors,
  LONG_POLLING_INTERVAL,
  LONG_STALE_TIME_INTERVAL,
} from '@beeriot/api-client';
import { appTheme } from '../theme';
import { TabsProvider } from '../navigation/tabs/providers/TabsProvider';
import {
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

if (process.env.NODE_ENV === 'development') {
  devTools();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
      staleTime: LONG_STALE_TIME_INTERVAL,
      refetchInterval: LONG_POLLING_INTERVAL,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

interface AppProviderProps {
  isStorybook?: boolean;
  children: React.ReactNode;
}

interface AppMemoryRouterProps {
  children: React.ReactNode;
}

const makeAppRouter = ({ isStorybook, children }: AppProviderProps) => {
  return isStorybook
    ? createMemoryRouter(createRoutesFromElements(children), {
        initialEntries: ['/'],
      })
    : createBrowserRouter(createRoutesFromElements(children));
};

export function AppProvider({ isStorybook, children }: AppProviderProps) {
  setupAxiosInterceptors(queryClient);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <MuiLocalizationProvider dateAdapter={AdapterDateFns}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider>
            <TabsProvider>
              <RouterProvider
                router={makeAppRouter({ isStorybook, children })}
              />
            </TabsProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </MuiLocalizationProvider>
    </ThemeProvider>
  );
}
